<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="2"></v-col>
      <v-col cols="12" sm="12" md="8">
        <v-card class="mx-auto my-5 mb-16" max-width="1000">
          <v-app-bar flat>
            <span class="headline">{{ title }}</span>
          </v-app-bar>
          <v-card-text>
            <v-form ref="form">
              <v-container>
                <v-row>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="form.name"
                      :label="$t('proof.form.name')"
                      :rules="[(v) => !!v || $t('general.fieldNeedsToBeFilled')]"
                      outlined dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.identifier"
                      :label="$t('proof.form.identifier')"
                      outlined dense
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="6" md="3">
                    <v-text-field
                      v-model.number="form.interval"
                      :label="$t('proof.form.interval')"
                      type="number"
                      :rules="[(v) => !!v || $t('general.fieldNeedsToBeFilled')]"
                      outlined dense
                      @change="recalcNextDate"
                    />
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-select
                      v-model="form.intervalUnit"
                      :items="intervalUnitItems"
                      :label="$t('proof.form.intervalUnit')"
                      outlined dense
                      @change="recalcNextDate"
                    />
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-menu v-model="lastDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDisplay(lastProofDatePicker)"
                          :label="$t('proof.form.lastProofDate')"
                          prepend-icon="mdi-calendar"
                          readonly outlined dense clearable
                          v-bind="attrs" v-on="on"
                          @click:clear="lastProofDatePicker = null"
                        />
                      </template>
                      <v-date-picker v-model="lastProofDatePicker" locale="de" no-title @input="onLastDatePicked" />
                    </v-menu>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-menu v-model="nextDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDisplay(nextProofDatePicker)"
                          :label="$t('proof.form.nextProofDate')"
                          prepend-icon="mdi-calendar"
                          readonly outlined dense clearable
                          v-bind="attrs" v-on="on"
                          @click:clear="nextProofDatePicker = null"
                        />
                      </template>
                      <v-date-picker v-model="nextProofDatePicker" locale="de" no-title @input="nextDateMenu = false" />
                    </v-menu>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-combobox
                      v-model="selectedTags"
                      :items="tagNames"
                      :label="$t('proof.form.tags')"
                      multiple chips small-chips deletable-chips clearable outlined dense
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-textarea v-model="form.additionalInfo" :label="$t('proof.form.additionalInfo')" outlined dense rows="3" />
                  </v-col>
                </v-row>

                <div class="subtitle-1 mt-2 mb-2">{{ $t("proof.form.prooferSection") }}</div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="form.proofer.companyName" :label="$t('proof.form.proofer.companyName')" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="form.proofer.contactName" :label="$t('proof.form.proofer.contactName')" outlined dense />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="form.proofer.phone" :label="$t('proof.form.proofer.phone')" outlined dense />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-textarea v-model="form.proofer.additionalInfo" :label="$t('proof.form.proofer.additionalInfo')" outlined dense rows="3" auto-grow />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.costs"
                      :label="$t('proof.form.costs')"
                      type="number"
                      min="0"
                      prefix="€"
                      outlined dense
                    />
                  </v-col>
                </v-row>

                <v-divider class="my-4" />
                <div class="subtitle-1 mb-2">{{ $t("proof.form.checklistSection") }}</div>

                <div v-if="form.checklist.length" class="tasks mb-2">
                  <div v-for="(item, idx) in form.checklist" :key="idx" class="task">
                    <span class="task__number">{{ idx + 1 }}.</span>
                    <template v-if="editingTaskIndex === idx">
                      <v-text-field
                        v-model="editTaskDraft"
                        dense
                        hide-details
                        autofocus
                        class="task__input"
                        @keydown.enter.prevent="confirmTaskEdit"
                        @keydown.esc.prevent="cancelTaskEdit"
                      />
                      <v-btn icon small color="success" :disabled="!editTaskDraft.trim()" @click="confirmTaskEdit">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small @click="cancelTaskEdit">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </template>
                    <template v-else>
                      <div class="task__text" @click="startTaskEdit(idx)">{{ item.task }}</div>
                      <v-btn icon small @click="startTaskEdit(idx)">
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="removeChecklistItem(idx)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </div>
                </div>

                <div class="d-flex align-center">
                  <v-text-field
                    ref="newTaskInput"
                    v-model="newTask"
                    :placeholder="$t('proof.form.newChecklistItem')"
                    outlined
                    dense
                    hide-details
                    @keydown.enter.prevent="addChecklistItem"
                  />
                  <v-btn
                    color="primary"
                    outlined
                    class="ml-2"
                    height="40"
                    :disabled="!newTask.trim()"
                    @click="addChecklistItem"
                  >
                    {{ $t("riskAssessment.advanced.addDescriptionPoint") }}
                  </v-btn>
                </div>

                <v-divider class="my-4" />
                <div class="subtitle-1 mb-3">{{ $t("proof.form.historySection") }}</div>

                <template v-if="historyWithIndex.length">
                  <v-card
                    v-for="entry in historyWithIndex"
                    :key="'h' + entry._idx + '-' + historyRefresh"
                    outlined
                    class="mb-3 pa-3"
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 4px">
                      <v-icon small class="mr-2">mdi-calendar</v-icon>
                      <span class="font-weight-medium mr-4">{{ formatDate(entry.date) }}</span>
                      <v-chip
                        v-for="(doc, dIdx) in entry.documents"
                        :key="dIdx"
                        small outlined class="mr-2 mb-1"
                        @click="downloadDoc(doc)"
                      >
                        <v-icon left small>mdi-paperclip</v-icon>
                        {{ doc.name || doc.location }}
                      </v-chip>
                      <span
                        v-if="!entry.documents || !entry.documents.length"
                        class="text-caption text--secondary"
                      >
                        {{ $t("proof.form.noDocuments") }}
                      </span>
                    </div>
                    <proofEntryDetails :entry="entry" class="mt-2" />
                    <div class="mt-2" style="max-width: 320px">
                      <v-file-input
                        :label="$t('proof.form.historyFiles')"
                        dense hide-details multiple small-chips
                        prepend-icon="mdi-paperclip"
                        :loading="uploadingIdx === entry._idx"
                        @change="(files) => uploadEntryDocs(entry, files)"
                      />
                    </div>
                  </v-card>
                </template>

                <v-card
                  v-for="(entry, idx) in historyEntries"
                  :key="'n' + idx"
                  outlined
                  class="mb-3 pa-3"
                >
                  <v-row align="center">
                    <v-col cols="12" md="4">
                      <v-menu
                        v-model="entry.dateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDisplay(entry.date)"
                            :label="$t('proof.form.historyDate')"
                            prepend-icon="mdi-calendar"
                            readonly outlined dense
                            v-bind="attrs" v-on="on"
                          />
                        </template>
                        <v-date-picker v-model="entry.date" locale="de" no-title @input="entry.dateMenu = false" />
                      </v-menu>
                    </v-col>
                    <v-col cols="12" md="7">
                      <v-file-input
                        v-model="entry.files"
                        :label="$t('proof.form.historyFiles')"
                        outlined dense multiple chips small-chips show-size
                        prepend-icon="mdi-paperclip"
                        @change="(files) => onEntryFilesChange(files, idx)"
                      />
                    </v-col>
                    <v-col cols="12" md="1" class="d-flex justify-center">
                      <v-btn icon color="error" @click="removeHistoryEntry(idx)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>

                <v-btn text color="primary" small @click="addHistoryEntry">
                  <v-icon small left>mdi-plus</v-icon>
                  {{ $t("proof.form.addHistoryEntry") }}
                </v-btn>
              </v-container>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="closeform">{{ $t("general.back") }}</v-btn>
            <v-btn color="primary" :loading="saving" @click="saveForm">
              <span v-if="$route.params.mode === 'edit'">{{ $t("general.saveChanges") }}</span>
              <span v-else>{{ $t("general.save") }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="2"></v-col>
    </v-row>
  </div>
</template>

<script>
import proofEntryDetails from "./proofEntryDetails.vue";

function getDefaultForm() {
  return {
    name: "",
    identifier: "",
    costs: null,
    interval: null,
    intervalUnit: "months",
    category: null,
    tags: [],
    additionalInfo: "",
    lastProofDate: null,
    nextProofDate: null,
    checklist: [],
    proofHistory: [],
    proofer: { companyName: "", contactName: "", phone: "", additionalInfo: "" },
  };
}

function toPickerDate(val) {
  if (!val) return null;
  return new Date(val).toISOString().substr(0, 10);
}

function todayPickerDate() {
  return new Date().toISOString().substr(0, 10);
}

function addIntervalToPicker(isoDate, interval, unit) {
  if (!isoDate || !interval) return null;
  const d = new Date(isoDate);
  if (unit === "weeks") {
    d.setDate(d.getDate() + interval * 7);
  } else {
    const day = d.getDate();
    d.setMonth(d.getMonth() + interval);
    if (d.getDate() < day) d.setDate(0);
  }
  return d.toISOString().substr(0, 10);
}

export default {
  components: { proofEntryDetails },
  data: () => ({
    form: getDefaultForm(),
    selectedTags: [],
    tags: [],
    title: "",
    saving: false,
    lastProofDatePicker: null,
    nextProofDatePicker: null,
    lastDateMenu: false,
    nextDateMenu: false,
    historyEntries: [],
    uploadingIdx: null,
    historyRefresh: 0,
    newTask: "",
    editingTaskIndex: null,
    editTaskDraft: "",
  }),
  computed: {
    tagNames() {
      return (this.tags || []).map((t) => t.tag);
    },
    intervalUnitItems() {
      return [
        { text: this.$t("proof.form.intervalUnits.weeks"), value: "weeks" },
        { text: this.$t("proof.form.intervalUnits.months"), value: "months" },
      ];
    },
    historyWithIndex() {
      return (this.form.proofHistory || [])
        .map((entry, _idx) => ({ ...entry, _idx }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
  async created() {
    const tagRes = await this.$store.dispatch("getAllProofTags");
    this.tags = tagRes?.data || tagRes || [];

    const { mode, id } = this.$route.params;
    if (mode === "edit" || mode === "copy") {
      const item = await this.$store.dispatch("getSingleProof", id);
      if (item) {
        this.form = {
          ...getDefaultForm(),
          ...item,
          intervalUnit: item.intervalUnit || "months",
          category: item.category?._id || item.category || null,
          checklist: Array.isArray(item.checklist)
            ? item.checklist.map((c) => ({ task: c.task }))
            : [],
          proofer: { ...getDefaultForm().proofer, ...(item.proofer || {}) },
        };
        this.lastProofDatePicker = toPickerDate(item.lastProofDate);
        this.nextProofDatePicker = toPickerDate(item.nextProofDate);
        this.selectedTags = (item.tags || []).map((t) => {
          if (t && typeof t === "object" && t.tag) return t.tag;
          if (typeof t === "string") {
            const doc = this.tags.find((x) => x._id === t);
            return doc ? doc.tag : t;
          }
          return "";
        }).filter(Boolean);

        if (mode === "copy") {
          this.title = this.$t("proof.copy");
          delete this.form._id;
          delete this.form.id;
          delete this.form.createdAt;
          delete this.form.updatedAt;
          this.form.lastProofDate = null;
          this.form.nextProofDate = null;
          this.form.proofHistory = [];
          this.form.checklist = this.form.checklist.map((c) => ({ task: c.task }));
          this.lastProofDatePicker = null;
          this.nextProofDatePicker = null;
        } else {
          this.title = this.$t("proof.edit");
        }
      }
    } else {
      this.title = this.$t("proof.addProof");
      if (this.$route.params.proofCategoryId) {
        this.form.category = this.$route.params.proofCategoryId;
      }
    }
  },
  methods: {
    formatDisplay(val) {
      if (!val) return "";
      const [y, m, d] = val.split("-");
      return `${d}.${m}.${y}`;
    },
    formatDate(val) {
      if (!val) return "";
      return this.formatDisplay(toPickerDate(val));
    },
    recalcNextDate() {
      if (!this.lastProofDatePicker || !this.form.interval) return;
      this.nextProofDatePicker = addIntervalToPicker(
        this.lastProofDatePicker,
        this.form.interval,
        this.form.intervalUnit,
      );
    },
    onLastDatePicked() {
      this.lastDateMenu = false;
      this.recalcNextDate();
    },
    addHistoryEntry() {
      this.historyEntries.push({
        date: todayPickerDate(),
        dateMenu: false,
        files: [],
        contents: [],
      });
    },
    removeHistoryEntry(idx) {
      this.historyEntries.splice(idx, 1);
    },
    addChecklistItem() {
      const task = this.newTask.trim();
      if (!task) return;
      this.form.checklist.push({ task });
      this.newTask = "";
      this.$nextTick(() => this.$refs.newTaskInput.focus());
    },
    startTaskEdit(idx) {
      this.editingTaskIndex = idx;
      this.editTaskDraft = this.form.checklist[idx].task;
    },
    confirmTaskEdit() {
      const task = this.editTaskDraft.trim();
      if (!task) return;
      this.form.checklist[this.editingTaskIndex].task = task;
      this.cancelTaskEdit();
    },
    cancelTaskEdit() {
      this.editingTaskIndex = null;
      this.editTaskDraft = "";
    },
    removeChecklistItem(idx) {
      this.form.checklist.splice(idx, 1);
      if (this.editingTaskIndex === idx) this.cancelTaskEdit();
      else if (this.editingTaskIndex !== null && this.editingTaskIndex > idx) {
        this.editingTaskIndex -= 1;
      }
    },
    async downloadDoc(doc) {
      const res = await this.$store.dispatch("getProofDocumentUrl", {
        proofId: this.form._id || this.form.id,
        location: doc.location,
        filetype: doc.filetype,
      });
      if (res?.url) window.open(res.url, "_blank");
    },
    readFileContent(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.readAsDataURL(file);
      });
    },
    async uploadEntryDocs(entry, files) {
      if (!files || !files.length) return;
      const id = this.form._id || this.form.id;
      if (!id) return;
      this.uploadingIdx = entry._idx;
      try {
        for (const file of files) {
          const content = await this.readFileContent(file);
          if (!content) continue;
          await this.$store.dispatch("uploadProofDocument", {
            id,
            content,
            name: file.name,
            filetype: file.name.split(".").pop().toLowerCase(),
            historyIndex: entry._idx,
          });
        }
        const fresh = await this.$store.dispatch("getSingleProof", id);
        if (fresh) this.form.proofHistory = fresh.proofHistory || [];
      } finally {
        this.uploadingIdx = null;
        this.historyRefresh += 1;
      }
    },
    onEntryFilesChange(files, idx) {
      if (!files || !files.length) {
        this.historyEntries[idx].contents = [];
        return;
      }
      const entry = this.historyEntries[idx];
      entry.contents = new Array(files.length).fill(null);
      files.forEach((file, i) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          entry.contents[i] = e.target.result.split(",")[1];
        };
        reader.readAsDataURL(file);
      });
    },
    closeform() {
      this.$router.back();
    },
    async processHistoryEntries(proofId) {
      const sorted = [...this.historyEntries]
        .filter((e) => e.date)
        .sort((a, b) => a.date.localeCompare(b.date));
      const baseIndex = (this.form.proofHistory || []).length;
      for (let i = 0; i < sorted.length; i++) {
        const entry = sorted[i];
        await this.$store.dispatch("logProof", { id: proofId, date: entry.date });
        for (let j = 0; j < (entry.files || []).length; j++) {
          const file = entry.files[j];
          const content = entry.contents[j];
          if (!content) continue;
          await this.$store.dispatch("uploadProofDocument", {
            id: proofId,
            content,
            name: file.name,
            filetype: file.name.split(".").pop().toLowerCase(),
            historyIndex: baseIndex + i,
          });
        }
      }
    },
    async saveForm() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        const payload = {
          ...this.form,
          tags: this.selectedTags.filter(Boolean),
          checklist: this.form.checklist
            .filter((c) => (c.task || "").trim())
            .map((c) => ({ task: c.task.trim(), completed: false })),
          costs: this.form.costs === "" ? null : this.form.costs,
          lastProofDate: this.lastProofDatePicker || null,
          nextProofDate: this.nextProofDatePicker || null,
        };

        if (this.$route.params.mode === "edit") {
          const id = payload._id || payload.id;
          await this.$store.dispatch("updateProof", { ...payload, id });
          if (this.historyEntries.length) await this.processHistoryEntries(id);
        } else {
          const created = await this.$store.dispatch("createProof", payload);
          if (created && this.historyEntries.length) {
            await this.processHistoryEntries(created._id || created.id);
          }
        }

        this.closeform();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.task {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 0 4px;
  border-radius: 4px;
}
.task:hover {
  background: rgba(0, 0, 0, 0.03);
}
.task__number {
  min-width: 24px;
  margin-right: 8px;
  font-weight: 500;
}
.task__text {
  flex: 1;
  cursor: text;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 6px 0;
}
.task__input {
  flex: 1;
  margin-top: 0;
  padding-top: 0;
}
</style>
